import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
    Dialog,
    Slide,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TextField from "@mui/material/TextField";
import Langs from "../../../app/lang/langs";
import { addEditClient, getClients } from "../../../components/clients/clientsSlice";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "14px";

const fieldLabelSx = {
    fontWeight: 700,
    mb: 0.75,
    letterSpacing: "0.04em",
    color: "rgba(0,0,0,0.45)",
    fontSize: "0.72rem",
    textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
        borderRadius: BORDER_RADIUS_INPUT,
        backgroundColor: "rgba(255,255,255,0.5)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
        transition: TRANSITION_SMOOTH,
        "& fieldset": {
            borderColor: "rgba(255,144,21,0.08)",
            transition: TRANSITION_SMOOTH,
        },
        "&:hover fieldset": {
            borderColor: "rgba(255,144,21,0.2)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "rgba(255,144,21,0.5)",
            boxShadow: "0 0 0 3px rgba(255,144,21,0.1)",
        },
        "&.Mui-focused": {
            backgroundColor: "rgba(255,255,255,0.10)",
        },
    },
    "& input": {
        fontSize: "16px",
        transition: TRANSITION_SMOOTH,
    },
    "& input::placeholder": {
        opacity: 0.45,
    },
};

const glassCardSx = {
    background: "rgba(255,255,255,0.65)",
    backdropFilter: "blur(20px) saturate(1.3)",
    WebkitBackdropFilter: "blur(20px) saturate(1.3)",
    borderRadius: "20px",
    border: "1px solid rgba(255,255,255,0.5)",
    boxShadow: "0 4px 24px rgba(0,0,0,0.04), 0 1px 4px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.7)",
    p: 2.5,
    mb: 2,
    transition: TRANSITION_SMOOTH,
};

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function MobileEditClientModal({
    visible,
    setVisible,
    client: clientProp,
    setClient: setClientProp,
}) {
    const dispatch = useDispatch();

    const [client, setClient] = useState({ id: null, nom: "", vfact: "" });
    const [nameError, setNameError] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        if (clientProp) {
            setClient(clientProp);
        } else {
            setClient({ id: null, nom: "", vfact: "" });
        }
    }, [clientProp]);

    /* ---- Handlers ---- */
    const handleEditClient = () => {
        if (client && client.id && client.nom.trim() !== "") {
            dispatch(
                addEditClient({ id: client.id, name: client.nom, vfact: client.vfact })
            ).then((response) => {
                const responseData = response.payload.responseData;
                if (responseData && responseData.status == 1) {
                    dispatch(getClients());
                }
            });
            handleClose();
        } else {
            setNameError(true);
        }
    };

    const handleDeleteClient = () => {
        if (client && client.id && client.nom.trim() !== "") {
            dispatch(addEditClient({ id: client.id, active: 0 })).then(
                (response) => {
                    const responseData = response.payload.responseData;
                    if (responseData && responseData.status == 1) {
                        dispatch(getClients());
                    }
                }
            );
            handleClose();
        } else {
            setNameError(true);
        }
    };

    const handleClose = () => {
        setShowDeleteConfirm(false);
        setVisible(false);
        setClientProp(null);
        setNameError(false);
        setClient({ id: null, nom: "", vfact: "" });
    };

    return (
        <>
            {/* ---- Main Edit Dialog ---- */}
            <Dialog
                fullScreen
                open={visible}
                onClose={handleClose}
                TransitionComponent={Transition}
                sx={{ zIndex: 1400 }}
                PaperProps={{
                    sx: {
                        background:
                            "linear-gradient(135deg, #f8f9fc 0%, #eef1f5 100%)",
                    },
                }}
            >
                {/* ---- Sticky AppBar ---- */}
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        background: "rgba(255,255,255,0.82)",
                        backdropFilter: "blur(24px) saturate(1.5)",
                        WebkitBackdropFilter: "blur(24px) saturate(1.5)",
                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                        color: "text.primary",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: "2px",
                            background: "linear-gradient(90deg, transparent, rgba(255,144,21,0.3), transparent)",
                        },
                    }}
                >
                    <Toolbar sx={{ minHeight: 56 }}>
                        <IconButton
                            edge="start"
                            onClick={handleClose}
                            aria-label="close"
                            sx={{ mr: 1 }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography
                            variant="h6"
                            sx={{
                                flex: 1,
                                fontWeight: 700,
                                fontSize: "1.1rem",
                                letterSpacing: "-0.02em",
                            }}
                        >
                            <Langs str="EDIT_CLIENT" />
                        </Typography>
                        <IconButton
                            onClick={() => setShowDeleteConfirm(true)}
                            aria-label="delete"
                            sx={{
                                color: "#d32f2f",
                            }}
                        >
                            <DeleteOutlineIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>

                {/* ---- Scrollable content ---- */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        px: 2,
                        pt: 3,
                        pb: "120px",
                        mt: 0,
                    }}
                >
                    <Box sx={glassCardSx}>
                        {/* Name field */}
                        <Typography sx={fieldLabelSx}>
                            <Langs str="Nom" />
                        </Typography>
                        <TextField
                            fullWidth
                            value={client.nom}
                            onChange={(e) => {
                                setClient({ ...client, nom: e.target.value });
                                if (nameError) setNameError(false);
                            }}
                            placeholder="Nom"
                            error={nameError}
                            helperText={nameError ? "Ce champ est requis" : ""}
                            sx={glassmorphicTextFieldSx}
                            inputProps={{ style: { fontSize: "16px" } }}
                        />

                        {/* V-FACT field */}
                        <Typography sx={fieldLabelSx}>
                            <Langs str="V-FACT" />
                        </Typography>
                        <TextField
                            fullWidth
                            value={client.vfact || ""}
                            onChange={(e) =>
                                setClient({ ...client, vfact: e.target.value })
                            }
                            placeholder="V-FACT"
                            sx={glassmorphicTextFieldSx}
                            inputProps={{ style: { fontSize: "16px" } }}
                        />
                    </Box>
                </Box>

                {/* ---- Sticky bottom buttons ---- */}
                <Box
                    sx={{
                        position: "sticky",
                        bottom: 0,
                        px: 2,
                        py: 2,
                        background: "rgba(255,255,255,0.88)",
                        backdropFilter: "blur(24px) saturate(1.4)",
                        WebkitBackdropFilter: "blur(24px) saturate(1.4)",
                        borderTop: "1px solid rgba(255,255,255,0.5)",
                        boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
                        display: "flex",
                        gap: 1.5,
                    }}
                >
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleClose}
                        sx={{
                            borderRadius: "14px",
                            minHeight: 48,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            borderColor: "rgba(0,0,0,0.15)",
                            color: "text.primary",
                            "&:active": { transform: "scale(0.97)" },
                        }}
                    >
                        <Langs str="CANCEL" />
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleEditClient}
                        sx={{
                            borderRadius: "14px",
                            minHeight: 48,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                            boxShadow: "0 4px 16px rgba(255,144,21,0.35), 0 1px 3px rgba(255,144,21,0.2)",
                            "&:hover": {
                                background: "linear-gradient(135deg, #FFC100 0%, #FF9015 100%)",
                            },
                            "&:active": { transform: "scale(0.97)" },
                        }}
                    >
                        <Langs str="EDIT" />
                    </Button>
                </Box>
            </Dialog>

            {/* ---- Delete Confirmation Dialog ---- */}
            <Dialog
                open={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                sx={{ zIndex: 1500 }}
                PaperProps={{
                    sx: {
                        borderRadius: "24px",
                        mx: 2,
                        p: 3,
                        textAlign: "center",
                        background: "rgba(255,255,255,0.92)",
                        backdropFilter: "blur(24px) saturate(1.4)",
                        WebkitBackdropFilter: "blur(24px) saturate(1.4)",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
                        border: "1px solid rgba(255,255,255,0.5)",
                    },
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        fontSize: "1.05rem",
                        mb: 3,
                        px: 1,
                    }}
                >
                    <Langs str="DELETE_CLIENT_CONFIRM_MESSAGE" />
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => setShowDeleteConfirm(false)}
                        sx={{
                            borderRadius: "14px",
                            minHeight: 48,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            borderColor: "rgba(0,0,0,0.15)",
                            color: "text.primary",
                            "&:active": { transform: "scale(0.97)" },
                        }}
                    >
                        <Langs str="CANCEL" />
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleDeleteClient}
                        sx={{
                            borderRadius: "14px",
                            minHeight: 48,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            background: "linear-gradient(135deg, #d32f2f 0%, #e53935 100%)",
                            boxShadow: "0 4px 16px rgba(211,47,47,0.3), 0 1px 3px rgba(211,47,47,0.2)",
                            "&:hover": {
                                background: "linear-gradient(135deg, #c62828 0%, #d32f2f 100%)",
                            },
                            "&:active": { transform: "scale(0.97)" },
                        }}
                    >
                        <Langs str="Delete" />
                    </Button>
                </Box>
            </Dialog>
        </>
    );
}
